import { DocumentQueryUnavailableReason } from '@/interfaces/library/knowledgeBase'
import { knowledgeBase } from '@/controllers/library/knowledgeBase'
import { notify } from '@kyvg/vue3-notification'
import i18n from '@/locales'

const state = {
  isSidecarOpen: false,
  conversation: [],
  isLocked: false,
  status: {
    type: 'unavailable',
    reason: DocumentQueryUnavailableReason.DOCUMENT_NOT_FOUND,
    message: ''
  },
  isLoadingStatus: false,
  contentId: null,
  contentType: null,
  targetPageInDocument: null
}

const mutations = {
  SET_SIDECAR_OPEN(state, isOpen) {
    state.isSidecarOpen = isOpen
  },
  ADD_MESSAGE(state, message) {
    state.conversation.push(message)
  },
  UPDATE_LAST_MESSAGE(state, update) {
    const lastMessage = state.conversation[state.conversation.length - 1]
    Object.assign(lastMessage, update)
  },
  CLEAR_CONVERSATION(state) {
    state.conversation = []
  },
  SET_LOCKED(state, isLocked) {
    state.isLocked = isLocked
  },
  SET_STATUS(state, status) {
    state.status = status
  },
  SET_LOADING_STATUS(state, isLoading) {
    state.isLoadingStatus = isLoading
  },
  SET_CONTENT_CONTEXT(state, { contentId, contentType }) {
    state.contentId = contentId
    state.contentType = contentType
  },
  SET_TARGET_PAGE_IN_DOCUMENT(state, pageNumber) {
    state.targetPageInDocument = pageNumber
  }
}

const actions = {
  openSidecar({ commit }) {
    commit('SET_SIDECAR_OPEN', true)
  },
  closeSidecar({ commit }) {
    commit('SET_SIDECAR_OPEN', false)
  },
  async fetchStatus({ commit }, { contentType, contentId }) {
    commit('SET_LOADING_STATUS', true)
    commit('SET_CONTENT_CONTEXT', { contentType, contentId })
    try {
      const apiStatus = await knowledgeBase.getStatus(contentType, contentId)
      commit('SET_STATUS', apiStatus)
    } catch (error) {
      console.error('Failed to fetch knowledge base status:', error)
      commit('SET_STATUS', {
        type: 'unavailable',
        reason: DocumentQueryUnavailableReason.DOCUMENT_NOT_FOUND,
        message: 'Failed to retrieve status.'
      })
    } finally {
      commit('SET_LOADING_STATUS', false)
    }
  },
  async sendQuery({ commit, state }, query) {
    if (state.isLocked) return

    commit('SET_LOCKED', true)
    commit('ADD_MESSAGE', { id: Date.now(), sender: 'user', text: query })
    commit('ADD_MESSAGE', { id: Date.now() + 1, sender: 'ai', isTyping: true })

    try {
      const response = await knowledgeBase.postQuery(state.contentType, state.contentId, query)
      switch (response.type) {
        case 'general':
          commit('UPDATE_LAST_MESSAGE', { text: response.responseText, requestId: response.requestId, isTyping: false })
          break
        case 'rag':
          commit('UPDATE_LAST_MESSAGE', { text: response.formattedText, citations: response.citations, requestId: response.requestId, isTyping: false })
          if (!response.hasMoreTokens) {
            commit('SET_STATUS', {
              type: 'token_limit_exceeded',
              message: 'You have reached the token limit for this document.',
              reason: null
            })
          }
          break
        case 'summary':
          commit('UPDATE_LAST_MESSAGE', { text: response.documentSummary, requestId: response.requestId, isTyping: false })
          break
        case 'translation':
          commit('UPDATE_LAST_MESSAGE', { text: response.responseText, requestId: response.requestId, isTyping: false })
          break
        case 'error':
          commit('UPDATE_LAST_MESSAGE', { text: response.message, isTyping: false, isError: true })
          break
      }
    } catch (error) {
      console.error('Failed to send query:', error)
      commit('UPDATE_LAST_MESSAGE', { text: i18n.global.t('errorPageMessage'), isTyping: false, isError: true })
    } finally {
      commit('SET_LOCKED', false)
    }
  },
  async sendFeedback(context, { requestId, payload }) {
    try {
      await knowledgeBase.sendFeedback(requestId, payload)
    } catch (error) {
      console.error('Failed to send feedback:', error)
      notify({
        title: i18n.global.t('error'),
        text: i18n.global.t('feedbackError'),
        type: 'error',
        duration: 10000
      })
    }
  },
  navigateToPageInDocument({ commit }, pageNumber) {
    commit('SET_TARGET_PAGE_IN_DOCUMENT', pageNumber)
  },
  resetState({ commit }) {
    commit('SET_SIDECAR_OPEN', false)
    commit('CLEAR_CONVERSATION')
    commit('SET_LOCKED', false)
    commit('SET_STATUS', {
      type: 'unavailable',
      reason: DocumentQueryUnavailableReason.DOCUMENT_NOT_FOUND,
      message: ''
    })
    commit('SET_LOADING_STATUS', false)
    commit('SET_CONTENT_CONTEXT', { contentId: null, contentType: null })
    commit('SET_TARGET_PAGE_IN_DOCUMENT', null)
  }
}

const getters = {
  conversation: (state) => state.conversation,
  isKnowledgeBaseEnabled: (state) => state.status.type !== 'unavailable',
  isLoadingStatus: (state) => state.isLoadingStatus,
  isLocked: (state) => state.isLocked,
  isQueryable: (state) => state.status.type === 'available',
  isSidecarActionable: (state) => state.status.type === 'available' || state.status.type === 'token_limit_exceeded',
  isSidecarOpen: (state) => state.isSidecarOpen,
  status: (state) => state.status,
  statusMessage: (state) => state.status.message
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
